import { useEffect, useRef, useState } from "react";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Typography } from "antd";
const { Text } = Typography;
const Legend = ({ fillScale, showLegend }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [domain, setDomain] = useState(fillScale?.scale?.domain() || []);
  const parentWidth = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDomain(fillScale?.scale?.domain() || []);
  }, [fillScale]);

  useEffect(() => {
    setCurrentPage(0);
  }, [showLegend, fillScale]);

  if (!fillScale?.dataMapping) return null;
  if (!showLegend) return null;

  const pages = getPages(domain, parentWidth);

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === pages.length - 1;
  return (
    <div
      className="px-8 flex h-9 border-t gap-4 border-[#E5E5E5]"
      data-plot-legend
    >
      <div className="flex items-center gap-1" data-plot-legend-pagination>
        <button
          disabled={isFirstPage}
          onClick={() => setCurrentPage((prev) => prev - 1)}
        >
          <CaretUpOutlined
            style={{ fontSize: "12px", opacity: isFirstPage ? 0.2 : 1 }}
          />
        </button>
        <p className="text-[12px]">
          {currentPage + 1}/{pages.length}
        </p>
        <button
          disabled={isLastPage}
          onClick={() => setCurrentPage((prev) => prev + 1)}
        >
          <CaretDownOutlined
            style={{ fontSize: "12px", opacity: isLastPage ? 0.2 : 1 }}
          />
        </button>
      </div>
      <div
        ref={parentWidth}
        className="flex-1 flex w-full flex-row gap-4"
        data-plot-legend-items
      >
        {pages.map((page, pageIndex) =>
          page.labels.map((d, i) => (
            <div
              key={`${pageIndex}-${i}`}
              className={`items-center gap-1 ${
                pageIndex === currentPage ? "flex" : "hidden"
              }`}
              data-plot-legend-item
            >
              <div
                style={{
                  backgroundColor: fillScale.scale(d),
                  border: `1px solid ${fillScale.strokeScale(d)}`,
                }}
                className="w-[14px] h-[14px] rounded-xs"
                data-plot-legend-swatch
              ></div>

              <div className="max-w-20">
                <Text
                  ellipsis={{
                    tooltip: d,
                  }}
                  className="block max-w-20 text-[#2B4752]"
                  data-plot-legend-label
                >
                  {d}
                </Text>
              </div>
            </div>
          )),
        )}
      </div>
    </div>
  );
};

const getPages = (domain, parentWidth) => {
  if (!parentWidth.current) return [{ width: 0, labels: [] }];

  const legendWidth = (label: string) => getCharacterWidth("" + label) + 36;
  const containerWidth = parentWidth.current.clientWidth;

  const pages = domain.reduce(
    (
      acc: Array<{ width: number; labels: string[] }>,
      label: string,
      i: number,
    ) => {
      if (i === 0) {
        acc.push({ width: legendWidth(label), labels: [label] });
        return acc;
      }

      const currentPage = acc[acc.length - 1];
      const newItemWidth = legendWidth(label);

      if (currentPage.width + newItemWidth > containerWidth) {
        acc.push({ width: newItemWidth, labels: [label] });
      } else {
        currentPage.width += newItemWidth;
        currentPage.labels.push(label);
      }

      return acc;
    },
    [],
  );
  return pages;
};

const getCharacterWidth = (text: string) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = "12px Work Sans"; // Set the desired font and size
  const metrics = context.measureText(text); // Replace 'A' with your character
  const characterWidth = metrics.width;

  return characterWidth;
};

export default Legend;
